import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { navigateToLogin } from 'src/utils/navigation';
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  removeToken,
  removeRefreshToken,
} from 'src/utils/authStorage';
import plainAxios from './axios.plain';
import { API_ENDPOINTS } from './api/endpoints';

// Ensure baseURL uses HTTPS
const getBaseURL = () => {
  let baseURL = process.env.REACT_APP_API_BASE_URL || '';

  console.log('[getBaseURL] Original REACT_APP_API_BASE_URL:', baseURL);

  // If baseURL is empty, return empty (will cause error, but better than wrong protocol)
  if (!baseURL) {
    console.warn('REACT_APP_API_BASE_URL is not set');
    return '';
  }

  // Remove any existing protocol
  baseURL = baseURL.replace(/^https?:\/\//, '');

  // Always prepend https://
  const finalBaseURL = `https://${baseURL}`;
  console.log('[getBaseURL] Final baseURL:', finalBaseURL);

  return finalBaseURL;
};

const apiBaseURL = getBaseURL();

const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 10000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// getToken is now imported from authStorage

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};
const refreshToken = async () => {
  try {
    const response = await plainAxios.post(API_ENDPOINTS.AUTH.REFRESH, {
      refresh_token: getRefreshToken(),
    });
    if (response.data?.data?.tokens?.access_token) {
      setToken(response.data.data.tokens.access_token);
      if (response.data.data.tokens.refresh_token) {
        setRefreshToken(response.data.data.tokens.refresh_token);
      }
      return response.data.data.tokens.access_token;
    }

    throw new Error('No token received');
  } catch (error) {
    removeToken();
    removeRefreshToken();
    // Also clear any pending secondary-link flags from localStorage
    try {
      localStorage.removeItem('pendingSecondaryLink');
      localStorage.removeItem('pendingSecondaryLinkReturn');
    } catch (_) {
      // ignore
    }
    navigateToLogin();
    throw error;
  }
};

// Exported for testing
export const requestInterceptor = async (config) => {
  const newConfig = { ...config };

  // Debug log for all requests
  const fullURL = newConfig.baseURL
    ? `${newConfig.baseURL}${newConfig.url}`
    : newConfig.url;

  const token = getToken();
  if (
    token &&
    isTokenExpired(token) &&
    !newConfig.url?.includes('refresh-token')
  ) {
    try {
      const newToken = await refreshToken();
      newConfig.headers = {
        ...newConfig.headers,
        Authorization: `Bearer ${newToken}`,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  } else if (token) {
    newConfig.headers = {
      ...newConfig.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return newConfig;
};

export function setupInterceptors(instance) {
  instance.interceptors.request.use(requestInterceptor, (error) =>
    Promise.reject(error)
  );
  // Response interceptor
  instance.interceptors.response.use(
    (response) => response.data,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest.retryAttempt) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch((err) => {
              Promise.reject(err);
            });
        }

        originalRequest.retryAttempt = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);

          // Clear tokens from localStorage
          removeToken();
          removeRefreshToken();

          // Redirect to login
          navigateToLogin();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      const errorPayload = error.response?.data || error.message;
      const retryAfterHeaderRaw =
        error.response?.headers?.['retry-after'] ??
        error.response?.headers?.['Retry-After'];
      const retryAfterHeaderSeconds = Number.parseInt(retryAfterHeaderRaw, 10);

      // Normalize OTP error payload so UI can reliably read retry_after + status.
      let normalizedError = errorPayload;
      if (errorPayload && typeof errorPayload === 'object') {
        normalizedError = {
          http_status: error.response?.status,
          ...errorPayload,
        };
        if (
          Number.isFinite(retryAfterHeaderSeconds) &&
          !('retry_after' in normalizedError)
        ) {
          normalizedError.retry_after = retryAfterHeaderSeconds;
        }
      }

      // Error handling
      switch (error.response?.status) {
        case 400:
          console.error('Bad Request:', normalizedError);
          break;
        case 403:
          console.error('Forbidden:', normalizedError);
          break;
        case 404:
          console.error('Not Found:', normalizedError);
          break;
        case 500:
          console.error('Server Error:', normalizedError);
          break;
        default:
          console.error('Error:', normalizedError);
      }

      return Promise.reject(normalizedError);
    }
  );
}

export default api;
